import { Avatar, AvatarFallback } from '@mdvip/shared/components/ui/avatar';
import { initials } from '@mdvip/shared/lib/initials';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';

import { memberAge } from '@/lib/members/memberAge';
import type { PracticeMember } from '@/lib/members/members.types';

export interface MemberOptionRowProps {
  member: PracticeMember;
  /** Highlights this row as the currently active member — CNP-825's switcher, not this picker
   * (nobody is active yet while this modal's own list is showing). */
  selected?: boolean;
  className?: string;
}

/**
 * One member in a pick list: initials avatar, name, MDVIP ID sub-line, age. Purely presentational
 * — no click handling of its own, so it fits equally inside a plain `<button>` (the landing modal)
 * and a listbox option that isn't a button at all (CNP-825's combobox).
 */
export function MemberOptionRow({ member, selected, className }: MemberOptionRowProps) {
  const { t } = useTranslation('providers');
  const age = memberAge(member.dateOfBirth);
  const name = [member.firstName, member.lastName].filter(Boolean).join(' ');

  return (
    <div className={cn('flex min-w-0 flex-1 items-center gap-4', className)}>
      <Avatar className="size-9 shrink-0" aria-hidden="true">
        <AvatarFallback
          className={cn(
            'bg-gradient-to-br from-avatar-gradient-from to-avatar-gradient-to text-xs font-bold text-foreground',
            selected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
          )}
        >
          {initials({ firstName: member.firstName, lastName: member.lastName })}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1 truncate">
        <span className="block truncate text-sm font-medium text-foreground">{name}</span>
        {member.mdvipId && <span className="block truncate text-xs text-muted-foreground">{member.mdvipId}</span>}
      </span>
      {age !== null && (
        <span className="shrink-0 whitespace-nowrap text-sm text-muted-foreground">
          {t('members.row.age', { age })}
        </span>
      )}
    </div>
  );
}
